/******************************************************************************
 * AdminMachineDedupTab: super-admin screen for cleaning duplicate machine types
 * and brands. Endpoints (core, app module):
 *   GET  admin/machine_duplicates?kind=TYPE|BRAND -> report, read-only
 *   POST admin/machine_duplicates/merge           -> keep one row, retire the rest
 *   GET/PUT admin/machine_types|machine_brands    -> correct a label that is
 *                                                    simply wrong, not a twin
 *
 * Detection runs server-side and mirrors migration/settings_cleanup/cluster.py:
 * groups flagged `requiresReview` come back with no suggested survivor because
 * the tool declines to guess between e.g. PELLETEUSE 5T and 8T.
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { Button, Typography } from "@material-tailwind/react";
/***/

/* Components */
import Loading from "../../../../../../components/loading/Loading";
import { Table } from "../../../../../../components/table/Table";
import MachineDuplicateGroupRow from "./MachineDuplicateGroupRow";
import MachineReferentialList from "./MachineReferentialList";
/***/

/* Services */
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
/***/

/**
* AdminMachineDedupTab: machine type/brand deduplication tab
*/
export default function AdminMachineDedupTab() {
  const [kind, setKind] = useState<EasyTech.MachineReferentialKind>("TYPE");
  const [mode, setMode] = useState<"duplicates" | "catalog">("duplicates");
  const [catalogReload, setCatalogReload] = useState(0);
  const [report, setReport] = useState<EasyTech.MachineDuplicateReport | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Tracks the currently selected kind for async callbacks below. A plain closure over
  // `kind` would always match its own request (it fetched with that same kind), so it
  // cannot detect that the *selection* moved on while the request was in flight; this
  // ref reflects the latest render and lets late responses be compared against it.
  const kindRef = useRef(kind);
  kindRef.current = kind;

  /**
  * loadReport: fetch the duplicate report for the selected list
  */
  const loadReport = useCallback(() => {
    const requestedKind = kind;
    // setReport(null) unmounts every MachineDuplicateGroupRow before the new report
    // paints. MachineDuplicateGroupRow seeds survivorId once via useState and never
    // re-syncs it against group.members, so this remount is what keeps a stale
    // survivor from a previous group shape from ever being offered to the merge call.
    setReport(null);
    setLoadError(false);
    api.get("admin/machine_duplicates?kind=" + kind)
      .then((ret) => {
        const next = ret as EasyTech.MachineDuplicateReport;
        if (next.kind === kindRef.current) setReport(next);
      })
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.machineDedup.errors.load") });
        if (requestedKind === kindRef.current) setLoadError(true);
      });
  }, [kind]);
  /***/

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const TABLE_HEAD = [
    { label: t("admin:tools.machineDedup.table.labels"), orderKey: null },
    { label: t("admin:tools.machineDedup.table.members"), orderKey: null },
    { label: t("admin:tools.machineDedup.table.machines"), orderKey: null },
    { label: t("admin:tools.machineDedup.table.confidence"), orderKey: null },
    { label: t("admin:tools.machineDedup.table.actions"), orderKey: null }
  ];

  return (
    <div className="my-4">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex gap-2">
          <Button size="sm"
            data-testid="kind-TYPE"
            variant={kind === "TYPE" ? "filled" : "outlined"}
            onClick={() => setKind("TYPE")}>
            {t("admin:tools.machineDedup.kinds.TYPE")}
          </Button>
          <Button size="sm"
            data-testid="kind-BRAND"
            variant={kind === "BRAND" ? "filled" : "outlined"}
            onClick={() => setKind("BRAND")}>
            {t("admin:tools.machineDedup.kinds.BRAND")}
          </Button>
        </div>
        <Button size="sm" variant="outlined" data-testid="dedup-reload"
          onClick={() => mode === "catalog" ? setCatalogReload((token) => token + 1) : loadReport()}>
          {t("admin:tools.machineDedup.reload")}
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Button size="sm"
          data-testid="mode-duplicates"
          variant={mode === "duplicates" ? "filled" : "outlined"}
          onClick={() => setMode("duplicates")}>
          {t("admin:tools.machineDedup.modes.duplicates")}
        </Button>
        <Button size="sm"
          data-testid="mode-catalog"
          variant={mode === "catalog" ? "filled" : "outlined"}
          onClick={() => setMode("catalog")}>
          {t("admin:tools.machineDedup.modes.catalog")}
        </Button>
      </div>

      <Typography variant="small" className="text-gray-600 mb-4">
        {t(mode === "catalog"
          ? "admin:tools.machineDedup.catalog.notice"
          : "admin:tools.machineDedup.notice")}
      </Typography>

      {mode === "catalog" && (
        <MachineReferentialList kind={kind} reloadToken={catalogReload} />
      )}

      {mode === "duplicates" && (loadError ? (
        <div className="flex flex-col items-start gap-2" data-testid="dedup-error">
          <Typography variant="small" color="red">
            {t("admin:tools.machineDedup.errors.load")}
          </Typography>
          <Button size="sm" variant="outlined" data-testid="dedup-retry" onClick={loadReport}>
            {t("common:retry")}
          </Button>
        </div>
      ) : report ? (
        <>
          <div className="flex flex-wrap gap-6 mb-4" data-testid="dedup-summary">
            <Typography variant="small">
              {t("admin:tools.machineDedup.summary.groups", { count: report.groupCount })}
            </Typography>
            <Typography variant="small">
              {t("admin:tools.machineDedup.summary.rows", { count: report.duplicateRowCount })}
            </Typography>
            <Typography variant="small">
              {t("admin:tools.machineDedup.summary.review", { count: report.reviewRequiredCount })}
            </Typography>
          </div>

          <Table tableHead={TABLE_HEAD}
            emptyTable={report.groups.length === 0 ? t("admin:tools.machineDedup.empty") : false}>
            {report.groups.map((group) => (
              <MachineDuplicateGroupRow
                key={group.groupId + ":" + group.members.map((m) => m.id).join(",")}
                group={group}
                kind={kind}
                onMerged={loadReport} />
            ))}
          </Table>
        </>
      ) : <Loading />)}
    </div>
  );
}
/***/
